import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import PublicNavigation from '../components/PublicNavigation';
import './ProjectDetail.css';

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Interest form fields
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchProject();
  }, [id]);

  async function fetchProject() {
    try {
      const docRef = doc(db, 'projects', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setProject({ id: docSnap.id, ...docSnap.data() });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project:', error);
      setLoading(false);
    }
  }

  async function handleExpressInterest(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create interest record
      await addDoc(collection(db, 'projectInterests'), {
        projectId: id,
        projectTitle: project.title,
        contactName,
        contactEmail,
        organizationName,
        message,
        submittedAt: serverTimestamp(),
        status: 'pending'
      });

      // Create email notification
      await addDoc(collection(db, 'mail'), {
        to: ['president@tnfusa.org', 'vpprojects@tnfusa.org', 'treasurer@tnfusa.org'],
        message: {
          subject: `Project Interest: ${project.title}`,
          html: `
            <h2>New Project Interest Received</h2>
            <p>Someone has expressed interest in a project:</p>
            
            <h3>Project: ${project.title}</h3>
            
            <h4>Contact Information:</h4>
            <ul>
              <li><strong>Name:</strong> ${contactName}</li>
              <li><strong>Email:</strong> ${contactEmail}</li>
              <li><strong>Organization:</strong> ${organizationName}</li>
            </ul>
            
            <h4>Message:</h4>
            <p>${message}</p>
            
            <p><a href="mailto:${contactEmail}">Reply to ${contactName}</a></p>
          `
        }
      });

      alert('Thank you! Your interest has been submitted. Our team will contact you soon.');
      setShowInterestForm(false);
      
      // Reset form
      setContactName('');
      setContactEmail('');
      setOrganizationName('');
      setMessage('');
      
    } catch (error) {
      console.error('Error submitting interest:', error);
      alert('Failed to submit interest. Please try again or email us directly.');
    }
    
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div className="projects-container">
        <PublicNavigation />
        <div className="loading">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="projects-container">
        <PublicNavigation />
        <div className="error">Project not found.</div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <PublicNavigation />
      
      <div className="project-detail-content">
        <button onClick={() => navigate('/projects')} className="back-button">
          ← Back to Projects
        </button>

        <div className="project-detail-card">
          <div className="project-header">
            <div className="project-category-badge">{project.category}</div>
            <h1>{project.title}</h1>
            <p className="project-location">📍 {project.location}</p>
          </div>

          <div className="project-section">
            <h2>Description</h2>
            <p>{project.description}</p>
          </div>

          <div className="project-section">
            <h2>Requirements</h2>
            <ul className="requirements-list">
              {project.requirements?.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </div>

          <div className="project-meta-section">
            <div className="meta-item">
              <strong>Budget:</strong>
              <span>${project.budget?.toLocaleString() || 'TBD'}</span>
            </div>
            <div className="meta-item">
              <strong>Status:</strong>
              <span className="status-badge">{project.status}</span>
            </div>
          </div>

          {project.status === 'available' && (
            <div className="action-section">
              <button 
                onClick={() => setShowInterestForm(true)}
                className="interest-button"
              >
                Express Interest
              </button>
            </div>
          )}
        </div>

        {showInterestForm && (
          <div className="modal-overlay">
            <div className="modal-content interest-form-modal">
              <h2>Express Interest in This Project</h2>
              <p className="modal-subtitle">
                Fill out the form below and our team will contact you shortly.
              </p>
              
              <form onSubmit={handleExpressInterest} className="interest-form">
                <div className="form-group">
                  <label>Your Name *</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Organization/Chapter Name *</label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    required
                    placeholder="South Florida Chapter"
                  />
                </div>

                <div className="form-group">
                  <label>Message (Optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="4"
                    placeholder="Tell us why you're interested in this project..."
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    type="submit"
                    disabled={submitting}
                    className="confirm-button"
                  >
                    {submitting ? 'Submitting...' : 'Submit Interest'}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setShowInterestForm(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectDetail;